// Form validation utilities

export const validators = {
  required: (value, fieldName = 'This field') => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      return `${fieldName} is required`;
    }
    return null;
  },

  email: (value) => {
    if (!value) return null; // Let required handle empty values
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(value)) {
      return 'Please enter a valid email address';
    }
    return null;
  },

  minLength: (min) => (value, fieldName = 'This field') => {
    if (!value) return null; // Let required handle empty values
    if (value.length < min) {
      return `${fieldName} must be at least ${min} characters long`;
    }
    return null;
  },

  password: (value) => {
    if (!value) return null; // Let required handle empty values
    
    if (value.length < 8) {
      return 'Password must be at least 8 characters long';
    }
    
    if (!/(?=.*[a-z])/.test(value)) {
      return 'Password must contain at least one lowercase letter';
    }
    
    if (!/(?=.*[A-Z])/.test(value)) {
      return 'Password must contain at least one uppercase letter';
    }
    
    if (!/(?=.*\d)/.test(value)) {
      return 'Password must contain at least one number';
    }
    
    return null;
  },

  passwordMatch: (confirmPassword, password) => {
    if (!confirmPassword) return null; // Let required handle empty values
    if (!password) return null; // Can't validate match if original password is missing
    if (confirmPassword !== password) {
      return 'Passwords do not match';
    }
    return null;
  },

  checkbox: (value, fieldName = 'This field') => {
    if (!value) {
      return `${fieldName} must be checked`;
    }
    return null;
  }
};

export const validateField = (value, validatorConfigs, additionalParams = {}) => {
  for (const config of validatorConfigs) {
    let error;
    
    if (typeof config === 'function') {
      error = config(value, undefined, additionalParams);
    } else if (typeof config === 'object' && config.validator) {
      error = config.validator(value, config.fieldName, additionalParams);
    }
    
    if (error) {
      return error;
    }
  }
  
  return null;
};

export const validateForm = (formData, validationSchema) => {
  const errors = {};
  
  for (const [fieldName, validatorConfigs] of Object.entries(validationSchema)) {
    const value = formData?.[fieldName];
    const error = validateField(value, validatorConfigs, formData);
    if (error) {
      errors[fieldName] = error;
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// Common validation schemas for reuse
export const signUpValidationSchema = {
  firstName: [validators.required],
  lastName: [validators.required],
  email: [validators.required, validators.email],
  password: [validators.required, validators.password],
  confirmPassword: [
    validators.required,
    (value, fieldName, formData) => validators.passwordMatch(value, formData?.password)
  ],
  organizationName: [validators.required],
  agreeToTerms: [{ validator: validators.checkbox, fieldName: 'You must agree to the terms of service' }]
};

export const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  
  let score = 0;
  const checks = {
    length: password.length >= 8,
    lowercase: /[a-z]/.test(password),
    uppercase: /[A-Z]/.test(password),
    number: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password)
  };
  
  score = Object.values(checks).filter(Boolean).length;
  
  const strengthData = {
    0: { label: '', color: '' },
    1: { label: 'Very Weak', color: 'red' },
    2: { label: 'Weak', color: 'orange' },
    3: { label: 'Fair', color: 'yellow' },
    4: { label: 'Good', color: 'blue' },
    5: { label: 'Strong', color: 'green' }
  };
  
  return {
    score,
    ...strengthData[score],
    checks
  };
};